import React, { useEffect, useState } from 'react';
import {
  Brain,
  Filter,
  TrendingUp,
  RefreshCw,
  Loader2,
  Star,
  Target,
  Zap,
  AlertTriangle,
  CheckCircle,
  Activity,
  BarChart3,
  PieChart as PieChartIcon,
  Clock,
  Hash,
  Eye,
  Settings,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  Area,
  AreaChart
} from 'recharts';
import { api } from '../utils/api';

interface EnhancedRelevanceMetrics {
  totalAnalyzed: number;
  educationalFiltered: number;
  operationalBoosted: number;
  averageScore: number;
  contentTypeDistribution: Array<{
    type: string;
    count: number;
    percentage: number;
    color: string;
  }>;
  scoringComponents: Array<{
    component: string;
    averageScore: number;
    weight: number;
  }>;
  recentAnalysis: Array<{
    timestamp: string;
    content: string;
    contentType: string;
    finalScore: number;
    components: {
      contentTypeScore: number;
      temporalRelevanceScore: number;
      iocActionabilityScore: number;
      mitreConfidenceScore: number;
      attributionScore: number;
    };
    provider: string;
    processingTime: number;
  }>;
  providerStats: Array<{
    provider: string;
    requests: number;
    averageTime: number;
    successRate: number;
  }>;
  qualityMetrics: {
    averageConfidence: number;
    reprocessingRate: number;
    qualityGradeDistribution: Array<{
      grade: string;
      count: number;
    }>;
  };
  cachingStats: {
    hitRate: number;
    totalRequests: number;
    cacheMisses: number;
    averageResponseTime: number;
  };
}

const COLORS = {
  EDUCATIONAL: '#ef4444',
  OPERATIONAL: '#22c55e',
  ANALYTICAL: '#3b82f6',
  ADVISORY: '#f59e0b',
  MIXED_OPERATIONAL: '#84cc16',
  MIXED_EDUCATIONAL: '#f97316'
};

export function EnhancedRelevanceDashboard() {
  const [metrics, setMetrics] = useState<EnhancedRelevanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getEnhancedRelevanceMetrics();
      setMetrics(response);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch enhanced relevance metrics:', err);
      setError('Failed to load enhanced relevance metrics');

      // Generate mock data for testing
      setMetrics({
        totalAnalyzed: 2847,
        educationalFiltered: 623,
        operationalBoosted: 891,
        averageScore: 0.67,
        contentTypeDistribution: [
          { type: 'OPERATIONAL', count: 891, percentage: 31.3, color: COLORS.OPERATIONAL },
          { type: 'ANALYTICAL', count: 756, percentage: 26.5, color: COLORS.ANALYTICAL },
          { type: 'EDUCATIONAL', count: 623, percentage: 21.9, color: COLORS.EDUCATIONAL },
          { type: 'ADVISORY', count: 345, percentage: 12.1, color: COLORS.ADVISORY },
          { type: 'MIXED_OPERATIONAL', count: 154, percentage: 5.4, color: COLORS.MIXED_OPERATIONAL },
          { type: 'MIXED_EDUCATIONAL', count: 78, percentage: 2.7, color: COLORS.MIXED_EDUCATIONAL }
        ],
        scoringComponents: [
          { component: 'Content Type', averageScore: 0.72, weight: 30 },
          { component: 'Temporal Relevance', averageScore: 0.68, weight: 25 },
          { component: 'IOC Actionability', averageScore: 0.54, weight: 20 },
          { component: 'MITRE Confidence', averageScore: 0.41, weight: 15 },
          { component: 'Attribution', averageScore: 0.59, weight: 10 }
        ],
        recentAnalysis: [
          {
            timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
            content: 'APT29 actively exploiting CVE-2024-1234 in government networks',
            contentType: 'OPERATIONAL',
            finalScore: 0.87,
            components: {
              contentTypeScore: 0.9,
              temporalRelevanceScore: 0.85,
              iocActionabilityScore: 0.8,
              mitreConfidenceScore: 0.75,
              attributionScore: 0.85
            },
            provider: 'cloudflare',
            processingTime: 2400
          },
          {
            timestamp: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
            content: 'Was ist ein Botnet? Tutorial für Anfänger',
            contentType: 'EDUCATIONAL',
            finalScore: 0.23,
            components: {
              contentTypeScore: 0.1,
              temporalRelevanceScore: 0.3,
              iocActionabilityScore: 0.0,
              mitreConfidenceScore: 0.0,
              attributionScore: 0.0
            },
            provider: 'cloudflare',
            processingTime: 1800
          },
          {
            timestamp: new Date(Date.now() - 18 * 60 * 1000).toISOString(),
            content: 'Lazarus group deploys new backdoor targeting cryptocurrency exchanges',
            contentType: 'OPERATIONAL',
            finalScore: 0.79,
            components: {
              contentTypeScore: 0.9,
              temporalRelevanceScore: 0.8,
              iocActionabilityScore: 0.6,
              mitreConfidenceScore: 0.7,
              attributionScore: 0.9
            },
            provider: 'cloudflare',
            processingTime: 2100
          }
        ],
        providerStats: [
          { provider: 'cloudflare', requests: 2231, averageTime: 2150, successRate: 98.7 },
          { provider: 'deepseek', requests: 87, averageTime: 3400, successRate: 95.4 },
          { provider: 'emergency-rules', requests: 23, averageTime: 45, successRate: 100.0 }
        ],
        qualityMetrics: {
          averageConfidence: 0.78,
          reprocessingRate: 4.2,
          qualityGradeDistribution: [
            { grade: 'A', count: 1142 },
            { grade: 'B', count: 856 },
            { grade: 'C', count: 423 },
            { grade: 'D', count: 134 },
            { grade: 'F', count: 67 }
          ]
        },
        cachingStats: {
          hitRate: 32.1,
          totalRequests: 2847,
          cacheMisses: 1933,
          averageResponseTime: 1850
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading enhanced relevance metrics...</span>
        </div>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Metrics</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchMetrics}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Brain className="h-8 w-8 text-purple-600 mr-3" />
            Enhanced Relevance Scoring
          </h1>
          <p className="text-gray-600 mt-1">
            Multi-dimensional threat intelligence relevance analysis and filtering
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Link
            to="/prefilter-monitor"
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center space-x-2"
          >
            <Filter className="h-4 w-4" />
            <span>Pre-filter Monitor</span>
            <ExternalLink className="h-3 w-3" />
          </Link>
          <div className="text-sm text-gray-500">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </div>
          <button
            onClick={fetchMetrics}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Analyzed</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.totalAnalyzed.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <Filter className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Educational Filtered</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.educationalFiltered.toLocaleString()}</p>
              <p className="text-xs text-red-600">
                {metrics.totalAnalyzed > 0 ? ((metrics.educationalFiltered / metrics.totalAnalyzed) * 100).toFixed(1) : '0'}% filtered
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Operational Boosted</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.operationalBoosted.toLocaleString()}</p>
              <p className="text-xs text-green-600">
                {metrics.totalAnalyzed > 0 ? ((metrics.operationalBoosted / metrics.totalAnalyzed) * 100).toFixed(1) : '0'}% boosted
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Star className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Score</p>
              <p className="text-2xl font-bold text-gray-900">{(metrics.averageScore * 100).toFixed(1)}%</p>
              <p className="text-xs text-gray-500">relevance score</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Zap className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Cache Hit Rate</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.cachingStats.hitRate.toFixed(1)}%</p>
              <p className="text-xs text-indigo-600">cost optimization</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Type Distribution & Scoring Components */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <PieChartIcon className="h-5 w-5 text-blue-600 mr-2" />
              Content Type Distribution
            </h3>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={metrics.contentTypeDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {metrics.contentTypeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Target className="h-5 w-5 text-green-600 mr-2" />
              Scoring Components Performance
            </h3>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={metrics.scoringComponents} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="component"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  fontSize={12}
                />
                <YAxis />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${(value * 100).toFixed(1)}%`,
                    name === 'averageScore' ? 'Average Score' : name
                  ]}
                />
                <Bar dataKey="averageScore" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Provider Performance & Quality Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Settings className="h-5 w-5 text-gray-600 mr-2" />
              AI Provider Performance
            </h3>
          </div>
          <div className="space-y-4">
            {metrics.providerStats.map((provider, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${provider.provider === 'cloudflare' ? 'bg-orange-100' :
                      provider.provider === 'deepseek' ? 'bg-purple-100' : 'bg-gray-100'
                    }`}>
                    <Brain className={`h-4 w-4 ${provider.provider === 'cloudflare' ? 'text-orange-600' :
                        provider.provider === 'deepseek' ? 'text-purple-600' : 'text-gray-600'
                      }`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 capitalize">{provider.provider}</p>
                    <p className="text-sm text-gray-500">{provider.requests.toLocaleString()} requests</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{provider.successRate.toFixed(1)}%</p>
                  <p className="text-xs text-gray-500">{provider.averageTime}ms avg</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              Quality Assurance Metrics
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{(metrics.qualityMetrics.averageConfidence * 100).toFixed(1)}%</p>
              <p className="text-sm text-gray-600">Avg Confidence</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{metrics.qualityMetrics.reprocessingRate.toFixed(1)}%</p>
              <p className="text-sm text-gray-600">Reprocessing Rate</p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Quality Grade Distribution:</p>
            {metrics.qualityMetrics.qualityGradeDistribution.map((grade, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Grade {grade.grade}</span>
                <span className="text-sm font-medium text-gray-900">{grade.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Analysis */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Activity className="h-5 w-5 text-indigo-600 mr-2" />
            Recent Analysis Results
          </h3>
        </div>
        <div className="space-y-4">
          {metrics.recentAnalysis.map((analysis, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${analysis.contentType === 'EDUCATIONAL' ? 'bg-red-100 text-red-800' :
                      analysis.contentType === 'OPERATIONAL' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                    }`}>
                    {analysis.contentType}
                  </span>
                  <span className="text-sm text-gray-500">
                    {new Date(analysis.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-900">
                    Score: {(analysis.finalScore * 100).toFixed(1)}%
                  </span>
                  <span className="text-xs text-gray-500">
                    {analysis.processingTime}ms | {analysis.provider}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3 truncate">
                {analysis.content}
              </p>
              <div className="grid grid-cols-5 gap-2 text-xs">
                <div className="text-center">
                  <p className="text-gray-500">Content</p>
                  <p className="font-medium">{(analysis.components.contentTypeScore * 100).toFixed(0)}%</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500">Temporal</p>
                  <p className="font-medium">{(analysis.components.temporalRelevanceScore * 100).toFixed(0)}%</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500">IOC</p>
                  <p className="font-medium">{(analysis.components.iocActionabilityScore * 100).toFixed(0)}%</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500">MITRE</p>
                  <p className="font-medium">{(analysis.components.mitreConfidenceScore * 100).toFixed(0)}%</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500">Attribution</p>
                  <p className="font-medium">{(analysis.components.attributionScore * 100).toFixed(0)}%</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}